import React from "react";
import ModalPortal from "./modal-portal";
import CustomButton from "./customs/custom-button";

interface IConfirmDialog {
  /** The question, phrased so the confirm verb answers it. */
  title: string;
  /** One line on what happens next. Optional. */
  description?: string;
  /** Verb, not "OK" — "Delete", "Restart", "Confirm". */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  /** `destructive` paints the confirm red; use it for anything unrecoverable. */
  tone?: "default" | "destructive";
}

/**
 * The single confirm shape. Every yes/no popup in the app renders through
 * this so the scrim, panel, type scale and button order stay identical.
 */
const ConfirmDialog: React.FC<IConfirmDialog> = ({
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  loading = false,
  tone = "default",
}) => {
  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[99999] flex animate-scrim-in items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        onClick={onCancel}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="w-full max-w-[480px] animate-modal-in overflow-hidden rounded-2xl border border-edge bg-surface-elevated shadow-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 pb-5 pt-6">
            <h2 className="text-[22px] font-bold leading-tight text-text-primary">
              {title}
            </h2>
            {description ? (
              <p className="mt-2 text-[13px] font-medium text-text-secondary">
                {description}
              </p>
            ) : null}
          </div>

          {/* flex-col-reverse puts the confirm on top when stacked, without order-* hacks */}
          <div className="flex flex-col-reverse gap-3 border-t border-edge px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <CustomButton
              variant="secondary"
              disabled={loading}
              onClick={onCancel}
            >
              {cancelLabel}
            </CustomButton>
            <CustomButton
              variant={tone === "destructive" ? "destructive" : "primary"}
              loading={loading}
              disabled={loading}
              onClick={onConfirm}
            >
              {confirmLabel}
            </CustomButton>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConfirmDialog;
